import React, { useEffect, useState } from "react";
import { Box, Button, IconButton, useTheme } from "@mui/material";
import { api, useGetProfessionQuery } from "../../state/api";
import Header from "../../components/Header";
import { DataGrid } from "@mui/x-data-grid";
import CustomColumnMenu from "../../components/DataGridCustomColumnMenu";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import Switch from "@mui/material/Switch";
import { deleteProfessionAction } from "../../redux/actions/professionAction";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { usePostUpdateStatusMutation } from "../../state/api";

const Profession = () => {
  const theme = useTheme();
  const { data, isLoading } = useGetProfessionQuery();
  const [rowData, setRowData] = useState([]);
  const [pageSize, setPageSize] = useState(20);
  const dispatch = useDispatch();

  useEffect(() => {
    if (data && data.success) {
      const formattedMeetups = data.professions.map((profession) => ({
        ...profession,
      }));
      setRowData(formattedMeetups);
    }
  }, [data]);

  const handleEdit = async (professionId) => {
    try {
      navigate(`/profession/edit/${professionId}`);
    } catch (error) {
      console.log("Error navigating to edit", error);
      toast.error("Error navigating to edit");
    }
  };

  const handleDelete = async (_id) => {
    try {
      const success = await dispatch(deleteProfessionAction(_id));
      if (success) {
        toast.success("Profession deleted successfully");
        dispatch(api.util.invalidateTags(["Profession"]));
      } else {
        toast.error("Failed to delete profession");
      }
    } catch (error) {
      console.error("Error deleting profession:", error);
    }
  };

  const removeProfession = (_id) => {
    Swal.fire({
      title: "Confirm Removal",
      text: "Are you sure you want to remove this ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, remove it!",
    }).then((result) => {
      if (result.isConfirmed) {
        handleDelete(_id);
      }
    });
  };

  const [updateStatus] = usePostUpdateStatusMutation();

  const handleStatus = async (Id, isActive) => {
    const tag = "Profession";
    try {
      const response = await updateStatus({ Id, tag, isActive });
      // console.log(response);
      if (response.data.success) {
        setRowData((prevData) =>
          prevData.map((row) =>
            row._id === Id ? { ...row, isActive: !isActive } : row
          )
        );
        toast.success("Status updated successfully");
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const navigate = useNavigate();

  const navigateCreate = () => {
    navigate("/profession/create");
  };

  const columns = [
    {
      field: "title",
      headerName: "Title",
      flex: 0.5,
    },
    {
      field: "isActive",
      headerName: "IsActive?",
      flex: 0.5,
      renderCell: (params) => (
        <div>
          <Switch
            checked={params.value}
            onChange={() => handleStatus(params.row._id, params.row.isActive)}
            inputProps={{ "aria-label": "toggle status" }}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "green",
                "&:hover": {
                  backgroundColor: "rgba(0, 128, 0, 0.50)",
                },
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "green",
              },
              "& .MuiSwitch-switchBase": {
                color: "red",
                "&:hover": {
                  backgroundColor: "rgba(255, 0, 0, 0.5)",
                },
              },
              "& .MuiSwitch-switchBase + .MuiSwitch-track": {
                backgroundColor: "red",
              },
            }}
          />
          {params.value ? "T" : "F"}
        </div>
      ),
    },
    {
      field: "createdAt",
      headerName: "CreatedAt",
      flex: 0.5,
      renderCell: (params) => {
        const date = new Date(params.value);
        return format(date, "yyyy-MM-dd");
      },
    },
    {
      field: "updatedAt",
      headerName: "UpdatedAt",
      flex: 0.5,
      renderCell: (params) => {
        const date = new Date(params.value);
        return format(date, "yyyy-MM-dd");
      },
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      flex: 0.6,
      renderCell: (params) => (
        <div>
          <IconButton
            // variant="contained"
            color="info"
            onClick={() => handleEdit(params.row._id)}
          >
            <EditIcon size="large" />
          </IconButton>
          <IconButton
            variant="contained"
            color="secondary"
            onClick={() => removeProfession(params.row._id)}
          >
            <DeleteIcon size="small" sx={{ color: "red" }} />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <Box m="1.5rem 2.5rem">
      <ToastContainer />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap="1rem"
      >
        <Header title="PROFESSION MANAGEMENT" />
        <Button
          variant="contained"
          endIcon={<AddCircleIcon />}
          onClick={navigateCreate}
        >
          Add Profession
        </Button>
      </Box>

      <Box
        mt="40px"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.success,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.secondary[200]} !important`,
          },
        }}
      >
        <DataGrid
          loading={isLoading || !data}
          getRowId={(row) => row._id}
          rows={rowData || []}
          columns={columns}
          pagination
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[10, 20, 50, 100]}
          components={{
            ColumnMenu: CustomColumnMenu,
          }}
        />
      </Box>
    </Box>
  );
};

export default Profession;
